import { prefersReducedMotion } from './landing-motion.js';

const heroChatMotion = Object.freeze({
    duration: 1000,
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
});
const messageDuration = heroChatMotion.duration;
const heightDuration = heroChatMotion.duration;
const scenarioDuration = heroChatMotion.duration;
const titleDuration = heroChatMotion.duration;
const typingDuration = 320;
const easing = heroChatMotion.easing;
const subjectBlur = 'blur(4px)';
const bottomPadding = 12;

const scenarios = [
    {
        title: 'رزرو نوبت',
        messages: [
            'سلام 👋 برای رزرو نوبت، روز موردنظر خود را ارسال کنید.',
            'پنجشنبه عصر',
            'پنجشنبه ساعت ۱۷:۳۰ خالی است؛ مایل هستید این زمان ثبت شود؟',
            'بله، لطفاً ثبت کنید.',
            'نوبت شما برای پنجشنبه ساعت ۱۷:۳۰ ثبت شد.',
        ],
    },
    {
        title: 'فرم درخواست مشاوره',
        messages: [
            'سلام 👋 برای ثبت درخواست مشاوره، موضوع موردنظر خود را ارسال کنید.',
            'طراحی مسیر گفت‌وگوی ربات',
            'درخواست شما ثبت شد؛ لطفاً نام و شماره تماس خود را وارد کنید.',
            'علی رضایی، ۰۹۱۲۱۲۳۴۵۶۷',
            'اطلاعات شما دریافت شد؛ نتیجه بررسی برایتان ارسال خواهد شد.',
        ],
    },
    {
        title: 'ثبت‌نام رویداد',
        messages: [
            'سلام 👋 برای ثبت‌نام رویداد، نام کارگاه را ارسال کنید.',
            'کارگاه طراحی محصول',
            'دو ظرفیت باقی مانده است؛ لطفاً نام و شماره موبایل خود را وارد کنید.',
            'علی رضایی، ۰۹۱۲۱۲۳۴۵۶۷',
            'ثبت‌نام شما انجام شد؛ لینک ورود برایتان ارسال خواهد شد.',
        ],
    },
    {
        title: 'پشتیبانی مشتریان',
        messages: [
            'سلام 👋 چگونه می‌توانم به شما کمک کنم؟',
            'هزینه ارسال به تهران چقدر است؟',
            'هزینه ارسال ۷۵ هزار تومان است؛ سفارش‌های بالای ۵۰۰ هزار تومان رایگان ارسال می‌شوند.',
            'معمولاً چند روز طول می‌کشد تا سفارش به تهران برسد؟',
            'ارسال به تهران معمولاً ۱ تا ۲ روز کاری زمان می‌برد.',
        ],
    },
];

const toPixels = (value) => Number.parseFloat(value) || 0;

const getTranslateY = (element) => {
    const transform = window.getComputedStyle(element).transform;

    if (!transform || transform === 'none') {
        return 0;
    }

    const values = transform.match(/matrix(?:3d)?\(([^)]+)\)/)?.[1]?.split(',').map(Number);

    if (!values) {
        return 0;
    }

    return values.length === 16 ? values[13] : values[5] || 0;
};

export function initHeroChat(element) {
    if (!(element instanceof HTMLElement)) {
        return () => {};
    }

    const scenarioTitle = element.querySelector('[data-hero-chat-scenario-title]');
    const scrollLayer = element.querySelector('[data-hero-chat-scroll]');
    const chatWindow = element.querySelector('[data-hero-chat-window]');
    const stage = element.querySelector('[data-hero-chat-stage]');
    const messageList = element.querySelector('[data-hero-chat-messages]');
    const messages = [...element.querySelectorAll('[data-hero-chat-message]')];
    const typing = element.querySelector('[data-hero-chat-typing]');
    const messageTexts = messages.map((message) => message.querySelector('[data-hero-chat-message-text]'));

    if (!scenarioTitle || !scrollLayer || !chatWindow || !stage || !messageList || messages.length !== 5 || !typing || messageTexts.some((text) => !text)) {
        return () => {};
    }

    let isCleanedUp = false;
    let isRunning = false;
    let isVisible = true;
    let isPageVisible = !document.hidden;
    const isReady = element.dataset.heroChatReady !== 'false';
    let activeScenarioIndex = 0;
    let scrollFrame = null;
    const timers = new Set();
    const activeAnimations = new Map();
    const animatedItems = [...messages, typing];
    const reducedMotion = prefersReducedMotion();
    let isPageLoaded = document.readyState === 'complete';

    if (isReady) {
        chatWindow.classList.remove('invisible');
    }

    const cancelAnimation = (target) => {
        activeAnimations.get(target)?.cancel();
        activeAnimations.delete(target);
    };

    const cancelAllAnimations = () => {
        [...activeAnimations.values()].forEach((animation) => animation.cancel());
        activeAnimations.clear();
    };

    const clearTimeline = () => {
        timers.forEach((timer) => window.clearTimeout(timer));
        timers.clear();
        cancelAllAnimations();
    };

    const updateScrollTransform = () => {
        scrollFrame = null;

        if (isCleanedUp || reducedMotion) {
            return;
        }

        const { top } = scrollLayer.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const progress = Math.max(-1, Math.min(1, (top - viewportHeight * 0.45) / (viewportHeight * 0.42)));
        const translateY = (-progress * 52).toFixed(2);
        const rotateX = (progress * 18).toFixed(2);
        const scale = (1 - Math.abs(progress) * 0.025).toFixed(4);

        scrollLayer.style.transform = `perspective(1400px) translateY(${translateY}px) rotateX(${rotateX}deg) scale(${scale})`;
    };

    const scheduleScrollUpdate = () => {
        if (isCleanedUp || reducedMotion || scrollFrame !== null) {
            return;
        }

        if (typeof window.requestAnimationFrame !== 'function') {
            updateScrollTransform();

            return;
        }

        scrollFrame = window.requestAnimationFrame(updateScrollTransform);
    };

    const resetScrollTransform = () => {
        if (scrollFrame !== null && typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(scrollFrame);
        }

        scrollFrame = null;
        scrollLayer.style.transform = 'perspective(1400px) translateY(0px) rotateX(0deg) scale(1)';
    };

    const schedule = (callback, delay) => {
        const timer = window.setTimeout(() => {
            timers.delete(timer);
            callback();
        }, delay);

        timers.add(timer);
    };

    const animateElement = (target, keyframes, duration, finish) => {
        cancelAnimation(target);

        if (reducedMotion || typeof target.animate !== 'function') {
            finish?.();

            return null;
        }

        const animation = target.animate(keyframes, {
            duration,
            easing,
            fill: 'forwards',
        });

        activeAnimations.set(target, animation);
        animation.onfinish = () => {
            if (activeAnimations.get(target) !== animation) {
                return;
            }

            activeAnimations.delete(target);
            finish?.();
        };
        animation.oncancel = () => {
            if (activeAnimations.get(target) === animation) {
                activeAnimations.delete(target);
            }
        };

        return animation;
    };

    const setMessageVisibility = (message, isVisible, { immediate = false, afterHidden, duration = messageDuration } = {}) => {
        if (isCleanedUp) {
            return;
        }

        cancelAnimation(message);
        message.dataset.heroChatVisible = String(isVisible);
        message.setAttribute('aria-hidden', String(!isVisible));
        message.style.pointerEvents = isVisible ? 'auto' : 'none';

        if (isVisible) {
            message.hidden = false;

            if (immediate || reducedMotion) {
                message.style.opacity = '1';
                message.style.transform = 'translateY(0px)';

                return;
            }

            message.style.opacity = '0';
            message.style.transform = 'translateY(8px)';

            animateElement(message, [
                { opacity: 0, transform: 'translateY(8px)' },
                { opacity: 1, transform: 'translateY(0px)' },
            ], duration, () => {
                message.style.opacity = '1';
                message.style.transform = 'translateY(0px)';
            });

            return;
        }

        if (message.hidden || immediate || reducedMotion) {
            message.hidden = true;
            message.style.removeProperty('opacity');
            message.style.removeProperty('transform');
            afterHidden?.();

            return;
        }

        const currentOpacity = toPixels(window.getComputedStyle(message).opacity);
        const currentTranslateY = getTranslateY(message);

        animateElement(message, [
            { opacity: currentOpacity, transform: `translateY(${currentTranslateY}px)` },
            { opacity: 0, transform: 'translateY(8px)' },
        ], duration, () => {
            message.hidden = true;
            message.style.removeProperty('opacity');
            message.style.removeProperty('transform');
            afterHidden?.();
        });
    };

    const updateScenarioMessages = (scenario) => {
        messageTexts.forEach((messageText, index) => {
            messageText.textContent = scenario.messages[index];
        });
    };

    const resetMessages = () => {
        animatedItems.forEach((item) => setMessageVisibility(item, false, { immediate: true }));
    };

    const getChatHeight = () => {
        const windowStyles = window.getComputedStyle(chatWindow);
        const verticalPadding = toPixels(windowStyles.paddingTop) + toPixels(windowStyles.paddingBottom);
        const contentHeight = Math.max(stage.scrollHeight, messageList.scrollHeight);

        return Math.max(toPixels(windowStyles.minHeight), contentHeight + verticalPadding + bottomPadding);
    };

    const setWindowHeight = ({ immediate = false } = {}) => {
        const currentHeight = chatWindow.getBoundingClientRect().height;
        const targetHeight = getChatHeight();

        cancelAnimation(chatWindow);

        if (immediate || reducedMotion || Math.abs(currentHeight - targetHeight) < 1) {
            chatWindow.style.height = `${targetHeight}px`;

            return;
        }

        chatWindow.style.height = `${currentHeight}px`;

        animateElement(chatWindow, [
            { height: `${currentHeight}px` },
            { height: `${targetHeight}px` },
        ], heightDuration, () => {
            chatWindow.style.height = `${targetHeight}px`;
        });
    };

    const updateScenarioTitle = (title, { immediate = false } = {}) => {
        if (!scenarioTitle) {
            return;
        }

        if (immediate || reducedMotion) {
            cancelAnimation(scenarioTitle);
            scenarioTitle.textContent = title;
            scenarioTitle.style.opacity = '1';
            scenarioTitle.style.transform = 'translateY(0px)';
            scenarioTitle.style.filter = 'blur(0px)';

            return;
        }

        const currentOpacity = toPixels(window.getComputedStyle(scenarioTitle).opacity);
        const currentTranslateY = getTranslateY(scenarioTitle);

        animateElement(scenarioTitle, [
            { opacity: currentOpacity, transform: `translateY(${currentTranslateY}px)`, filter: 'blur(0px)' },
            { opacity: 0, transform: 'translateY(4px)', filter: subjectBlur },
        ], titleDuration, () => {
            scenarioTitle.textContent = title;
            animateElement(scenarioTitle, [
                { opacity: 0, transform: 'translateY(-4px)', filter: subjectBlur },
                { opacity: 1, transform: 'translateY(0px)', filter: 'blur(0px)' },
            ], titleDuration, () => {
                scenarioTitle.style.opacity = '1';
                scenarioTitle.style.transform = 'translateY(0px)';
                scenarioTitle.style.filter = 'blur(0px)';
            });
        });
    };

    const showAll = () => {
        updateScenarioMessages(scenarios[0]);
        updateScenarioTitle(scenarios[0].title, { immediate: true });
        resetMessages();
        messages.forEach((message) => setMessageVisibility(message, true, { immediate: true }));
        setWindowHeight({ immediate: true });
        stage.style.opacity = '1';
        stage.style.transform = 'translateY(0px)';
        stage.style.filter = 'blur(0px)';
    };

    const runCycle = () => {
        if (isCleanedUp || !isRunning || reducedMotion || !isVisible || !isPageVisible) {
            return;
        }

        clearTimeline();
        const activeScenario = scenarios[activeScenarioIndex];
        const shouldAnimateTitle = scenarioTitle.textContent !== activeScenario.title;

        updateScenarioMessages(activeScenario);
        resetMessages();
        updateScenarioTitle(activeScenario.title, { immediate: !shouldAnimateTitle });
        stage.style.opacity = '0';
        stage.style.transform = 'translateY(4px)';
        stage.style.filter = subjectBlur;

        animateElement(stage, [
            { opacity: 0, transform: 'translateY(4px)', filter: subjectBlur },
            { opacity: 1, transform: 'translateY(0px)', filter: 'blur(0px)' },
        ], scenarioDuration, () => {
            stage.style.opacity = '1';
            stage.style.transform = 'translateY(0px)';
            stage.style.filter = 'blur(0px)';
        });

        setMessageVisibility(messages[0], true);
        setWindowHeight();

        schedule(() => {
            setMessageVisibility(messages[1], true);
            setWindowHeight();
        }, 1400);
        schedule(() => {
            setMessageVisibility(typing, true, { duration: typingDuration });
            setWindowHeight();
        }, 2800);
        schedule(() => {
            setMessageVisibility(typing, false, {
                duration: typingDuration,
                afterHidden: () => {
                    if (isCleanedUp || !isRunning) {
                        return;
                    }

                    setMessageVisibility(messages[2], true);
                    setWindowHeight();
                },
            });
        }, 4000);
        schedule(() => {
            setMessageVisibility(messages[3], true);
            setWindowHeight();
        }, 5800);
        schedule(() => {
            setMessageVisibility(typing, true, { duration: typingDuration });
            setWindowHeight();
        }, 7200);
        schedule(() => {
            setMessageVisibility(typing, false, {
                duration: typingDuration,
                afterHidden: () => {
                    if (isCleanedUp || !isRunning) {
                        return;
                    }

                    setMessageVisibility(messages[4], true);
                    setWindowHeight();
                },
            });
        }, 8400);
        schedule(() => {
            animateElement(stage, [
                { opacity: 1, transform: 'translateY(0px)', filter: 'blur(0px)' },
                { opacity: 0, transform: 'translateY(-4px)', filter: subjectBlur },
            ], scenarioDuration, () => {
                if (isCleanedUp || !isRunning) {
                    return;
                }

                activeScenarioIndex = (activeScenarioIndex + 1) % scenarios.length;
                runCycle();
            });
        }, 10800);
    };

    const start = () => {
        if (isCleanedUp || isRunning || !isReady || !isVisible || !isPageVisible || !isPageLoaded) {
            return;
        }

        isRunning = true;
        scheduleScrollUpdate();

        if (reducedMotion) {
            showAll();

            return;
        }

        runCycle();
    };

    const stop = () => {
        clearTimeline();
        isRunning = false;
        resetScrollTransform();
        updateScenarioMessages(scenarios[activeScenarioIndex]);
        updateScenarioTitle(scenarios[activeScenarioIndex].title, { immediate: true });
        resetMessages();
        setWindowHeight({ immediate: true });
        stage.style.opacity = '1';
        stage.style.transform = 'translateY(0px)';
        stage.style.filter = 'blur(0px)';
    };

    const isInViewport = () => {
        const { bottom, height, top } = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        return height > 0 && bottom > 0 && top < viewportHeight;
    };

    const syncVisibility = () => {
        const nextIsVisible = isInViewport();

        if (nextIsVisible === isVisible && (!nextIsVisible || isRunning)) {
            return;
        }

        isVisible = nextIsVisible;
        isVisible ? start() : stop();
    };

    const handleLoad = () => {
        if (isPageLoaded || isCleanedUp) {
            return;
        }

        isPageLoaded = true;
        syncVisibility();
    };

    // Slow third-party images (e.g. the Enamad seal) can hold back the window load event, so don't wait for it forever.
    const loadFallbackTimer = isPageLoaded ? null : window.setTimeout(handleLoad, 1500);

    const intersectionObserver = typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(([entry]) => {
            isVisible = entry.isIntersecting;
            isVisible ? start() : stop();
        }, { threshold: 0.1 });

    const handleVisibilityChange = () => {
        isPageVisible = !document.hidden;
        isPageVisible ? start() : stop();
    };

    const handleScroll = () => {
        scheduleScrollUpdate();
        syncVisibility();
    };

    intersectionObserver?.observe(element);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    window.addEventListener('load', handleLoad, { once: true });
    if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(syncVisibility);
    } else {
        syncVisibility();
    }
    if (!intersectionObserver) {
        start();
    }

    return () => {
        if (isCleanedUp) {
            return;
        }

        stop();
        isCleanedUp = true;
        intersectionObserver?.disconnect();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        window.removeEventListener('load', handleLoad);
        window.clearTimeout(loadFallbackTimer);
        chatWindow.classList.add('invisible');
        resetScrollTransform();

        updateScenarioMessages(scenarios[0]);
        scenarioTitle.textContent = scenarios[0].title;

        messages.forEach((message) => {
            message.hidden = true;
            message.setAttribute('aria-hidden', 'true');
            message.removeAttribute('data-hero-chat-visible');
            message.style.removeProperty('pointer-events');
            message.style.removeProperty('opacity');
            message.style.removeProperty('transform');
        });

        typing.hidden = true;
        typing.setAttribute('aria-hidden', 'true');
        typing.removeAttribute('data-hero-chat-visible');
        typing.style.removeProperty('pointer-events');
        typing.style.removeProperty('opacity');
        typing.style.removeProperty('transform');

        scenarioTitle?.style.removeProperty('opacity');
        scenarioTitle?.style.removeProperty('transform');
        scenarioTitle?.style.removeProperty('filter');
        chatWindow.style.removeProperty('height');
        scrollLayer.style.removeProperty('transform');
        stage.style.removeProperty('opacity');
        stage.style.removeProperty('transform');
        stage.style.removeProperty('filter');
    };
}
